import { z } from 'zod';
import { AGE_BANDS, ANAESTHETIC_PRIORITIES, ANAESTHETIC_SETTINGS, CASE_TYPES, COMMUNICATION_CATEGORIES, COMMUNICATION_OUTCOMES, COMMUNICATION_PARTICIPANTS, EDT_ACTIVITIES, LEARNER_GRADES, MAX_EDT_DURATION_MINUTES, REFERRAL_REASONS, SIDES, SOURCES, } from './enums.js';
import { procedureByKey } from './procedures.js';
/**
 * Validation, defined once and used by react-hook-form on the client and as the
 * server's request validator. One definition, two consumers.
 *
 * The guiding rule from DATA_MODEL.md: **everything is optional unless listed
 * as required.** Every additional required field costs adoption, and this app
 * competes with a spreadsheet. Do not add one without an explicit instruction.
 *
 * Required on a Case: caseType, title, occurredAt, ageBand, capabilityLevel.
 * Required on a Procedure: procedureKey, occurredAt, ageBand, capabilityLevel.
 * Required on a Communication: category, occurredAt, ageBand, capabilityLevel.
 * Required on an EDT entry: activity, title, occurredAt.
 */
/** No backdating limit — a trainee catching up on a month of nights is normal. */
const EARLIEST_ENTRY = new Date('1990-01-01T00:00:00.000Z');
/**
 * A day of slack for a device whose clock runs fast. Beyond that the sync
 * engine reports CLOCK_SKEW rather than silently accepting an entry dated next
 * year, which would sit at the top of the list forever.
 */
const FUTURE_TOLERANCE_MS = 24 * 60 * 60 * 1000;
const occurredAt = z
    .date()
    .min(EARLIEST_ENTRY, 'That date is before ICULogbook supports.')
    .refine((value) => value.getTime() <= Date.now() + FUTURE_TOLERANCE_MS, 'That date is in the future. Check your device clock.');
const capabilityLevel = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
/**
 * ICS level of care, 0–3. Literals rather than `z.number().min(0).max(3)` for
 * the same reason as `capabilityLevel` above: the inferred type is `CareLevel`
 * and not `number`. Note that 0 is a valid level, so nothing here may treat a
 * falsy value as absent.
 */
const careLevel = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
/*
 * Written as literals rather than `z.number().min(1).max(14)` so the inferred
 * type is `HilloNumber` and not `number`. That matters: the record types say
 * `HilloNumber[]`, and a schema that infers `number[]` forces a cast at every
 * point where validated input becomes a record — which is exactly where a real
 * mistake would slip through unnoticed.
 */
const hilloNumber = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
    z.literal(8),
    z.literal(9),
    z.literal(10),
    z.literal(11),
    z.literal(12),
    z.literal(13),
    z.literal(14),
]);
const hillos = z.array(hilloNumber).max(14).optional();
const note = z.string().trim().max(500).optional();
/** Fields every entry has, whichever kind it is. */
const entryFields = {
    occurredAt,
    ageBand: z.enum(AGE_BANDS),
    capabilityLevel,
    hillos,
    note,
};
/** Fields the repository sets, never a person. */
const storedFields = {
    id: z.string().uuid(),
    userId: z.string().uuid(),
    source: z.enum(SOURCES),
    importBatchId: z.string().uuid().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().optional(),
};
const caseFields = {
    caseType: z.enum(CASE_TYPES),
    title: z
        .string()
        .trim()
        .min(1, 'Give the case a short title.')
        .max(140, 'Keep the title under 140 characters.'),
    reasonsForReferral: z.array(z.enum(REFERRAL_REASONS)).optional(),
    careLevel: careLevel.optional(),
    teachingGrades: z.array(z.enum(LEARNER_GRADES)).optional(),
};
const procedureFields = {
    caseId: z.string().uuid().optional(),
    procedureKey: z.string().refine((key) => procedureByKey(key) !== undefined, {
        message: 'Choose a procedure from the list.',
    }),
    variant: z.string().optional(),
    side: z.enum(SIDES).optional(),
    ultrasoundGuided: z.boolean().optional(),
    attempts: z.number().int().min(1).max(10).optional(),
    successful: z.boolean().optional(),
    complications: z.string().trim().max(300).optional(),
    indication: z.string().trim().max(200).optional(),
    teachingGrades: z.array(z.enum(LEARNER_GRADES)).optional(),
};
/**
 * A significant discussion — most often withdrawal or withholding of
 * treatment. `withWhom` is roles rather than names: rejecting a free-text
 * field here is not enough on its own, since a trainee could still write a
 * name into `note`, but a fixed list of roles is what the field itself
 * invites, and that is the field most likely to be reached for first.
 */
const communicationFields = {
    category: z.enum(COMMUNICATION_CATEGORIES),
    withWhom: z.array(z.enum(COMMUNICATION_PARTICIPANTS)).optional(),
    outcome: z.enum(COMMUNICATION_OUTCOMES).optional(),
    teachingGrades: z.array(z.enum(LEARNER_GRADES)).optional(),
};
/*
 * EDT entries share `occurredAt`, `hillos` and a note with clinical entries,
 * but not `ageBand` or `capabilityLevel` — there is no patient, so neither
 * means anything. `entryFields` therefore cannot be reused wholesale.
 */
const edtFields = {
    occurredAt,
    activity: z.enum(EDT_ACTIVITIES),
    title: z
        .string()
        .trim()
        .min(1, 'Give this a short title.')
        .max(140, 'Keep the title under 140 characters.'),
    /**
     * Optional end of a period of work. Validated against `occurredAt` by the
     * refinement on the schema below, not here — a field cannot see its siblings.
     */
    endsAt: z.date().optional(),
    durationMinutes: z.number().int().min(1).max(MAX_EDT_DURATION_MINUTES).optional(),
    hillos,
    /* Longer than a clinical note: on an EDT entry this *is* the record. */
    note: z.string().trim().max(2000).optional(),
};
/*
 * An anaesthetic case shares the patient fields with an ICM case — there is a
 * patient, so an age band and a capability level both mean something — but has
 * no case type and no reason for referral.
 */
const anaestheticFields = {
    occurredAt,
    ageBand: z.enum(AGE_BANDS),
    capabilityLevel,
    setting: z.enum(ANAESTHETIC_SETTINGS),
    title: z
        .string()
        .trim()
        .min(1, 'Give this a short title.')
        .max(140, 'Keep the title under 140 characters.'),
    priority: z.enum(ANAESTHETIC_PRIORITIES).optional(),
    /* Literals rather than min/max, for the same reason as capabilityLevel above:
     * the inferred type is `AsaGrade` and not `number`, so validated input drops
     * straight into a record with no cast. `ASA_GRADES` in enums.ts is the list
     * the UI renders from; these two must agree, and a test asserts they do. */
    asa: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]).optional(),
    teachingGrades: z.array(z.enum(LEARNER_GRADES)).optional(),
    hillos,
    note,
};
export function refineProcedure(value, ctx) {
    const definition = procedureByKey(value.procedureKey);
    if (definition === undefined)
        return;
    if (value.variant !== undefined) {
        const allowed = definition.variants ?? [];
        if (!allowed.includes(value.variant)) {
            ctx.addIssue({
                code: 'custom',
                path: ['variant'],
                message: allowed.length === 0
                    ? `${definition.label} does not have variants.`
                    : `Choose one of: ${allowed.join(', ')}.`,
            });
        }
    }
    if (value.side !== undefined && definition.sideApplicable !== true) {
        ctx.addIssue({
            code: 'custom',
            path: ['side'],
            message: `Side does not apply to ${definition.label}.`,
        });
    }
    if (value.ultrasoundGuided !== undefined && definition.usGuidanceApplicable !== true) {
        ctx.addIssue({
            code: 'custom',
            path: ['ultrasoundGuided'],
            message: `Ultrasound guidance does not apply to ${definition.label}.`,
        });
    }
}
/* ------------------------------------------------------------ full records */
/*
 * The plain object shapes are exported as well as the finished schemas, because
 * the sync wire format is these same fields with dates arriving as ISO strings.
 * Deriving that from here is what stops the two definitions drifting apart.
 */
export const caseRecordShape = z.object({ ...storedFields, ...entryFields, ...caseFields });
export const procedureRecordShape = z.object({
    ...storedFields,
    ...entryFields,
    ...procedureFields,
});
export const communicationRecordShape = z.object({
    ...storedFields,
    ...entryFields,
    ...communicationFields,
});
export const edtEntryRecordShape = z.object({ ...storedFields, ...edtFields });
export const anaestheticCaseRecordShape = z.object({ ...storedFields, ...anaestheticFields });
export const caseSchema = caseRecordShape;
export const procedureSchema = procedureRecordShape.superRefine(refineProcedure);
export const communicationSchema = communicationRecordShape;
export const edtEntrySchema = edtEntryRecordShape;
export const anaestheticCaseSchema = anaestheticCaseRecordShape;
/* -------------------------------------------------------------- form input */
export const caseInputSchema = z.object({ ...entryFields, ...caseFields });
export const procedureInputSchema = z
    .object({ ...entryFields, ...procedureFields })
    .superRefine(refineProcedure);
export const communicationInputSchema = z.object({ ...entryFields, ...communicationFields });
export const edtEntryInputSchema = z.object({ ...edtFields }).refine(
/*
 * A period that ends before it starts is a typo, not a record. Same value
 * shown to the trainee as the report screen's date boxes use, so the two
 * read alike. Equal dates are fine — a single day is a legitimate span.
 */
(entry) => entry.endsAt === undefined || entry.endsAt.getTime() >= entry.occurredAt.getTime(), { message: 'That period ends before it starts.', path: ['endsAt'] });
export const anaestheticCaseInputSchema = z.object({ ...anaestheticFields });
//# sourceMappingURL=schemas.js.map